import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, ChevronLeft, ChevronRight, Plus, Clock, MapPin, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgendaPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API}/agenda`, { credentials: 'include' });
      if (res.ok) {
        const sessions = await res.json();
        setEvents(sessions.map(s => ({
          id: s.session_id,
          title: s.title,
          date: parseSessionDate(s.date, s.start_time),
          endDate: parseSessionDate(s.date, s.end_time),
          type: s.type === 'distanciel' ? 'meeting' : s.status === 'completed' ? 'evaluation' : 'session',
          location: s.location,
          participants: s.max_capacity || 0,
          trainer: s.trainer,
          course_title: s.course_title,
          status: s.status
        })));
      }
    } catch (e) { console.error(e); }
  };

  const parseSessionDate = (dateStr, timeStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = (timeStr || '09:00').split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay() || 7;

    const days = [];
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 2; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonthLastDay - i), isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  };

  const getEventsForDate = (date) => events.filter(e => e.date.toDateString() === date.toDateString());

  const getEventColor = (type) => {
    switch (type) {
      case 'session': return 'bg-teal-500';
      case 'meeting': return 'bg-blue-500';
      case 'evaluation': return 'bg-purple-500';
      default: return 'bg-slate-500';
    }
  };

  const formatTime = (date) => date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const days = getDaysInMonth(currentDate);
  const today = new Date();

  const upcomingEvents = events
    .filter(e => e.date >= today && e.status !== 'completed')
    .sort((a, b) => a.date - b.date)
    .slice(0, 5);

  return (
    <Layout>
      <div className="space-y-6" data-testid="agenda-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Agenda</h1>
            <p className="text-slate-600 mt-1">Planifiez et gérez vos sessions de formation</p>
          </div>
          <Button className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="new-event-btn">
            <Plus className="h-4 w-4 mr-2" />Nouvel événement
          </Button>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-4">
                  <Button variant="outline" size="icon" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h2 className="text-xl font-semibold text-slate-900">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  <Button variant="outline" size="icon" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Aujourd'hui</Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map((day) => (
                    <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">{day}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day, index) => {
                    const dayEvents = getEventsForDate(day.date);
                    const isToday = day.date.toDateString() === today.toDateString();
                    return (
                      <div key={index} className={`min-h-[100px] p-2 rounded-lg border transition-colors cursor-pointer ${
                        day.isCurrentMonth ? 'bg-white hover:bg-slate-50 border-slate-200' : 'bg-slate-50 border-slate-100'
                      } ${isToday ? 'ring-2 ring-teal-500' : ''}`}>
                        <span className={`text-sm font-medium ${
                          day.isCurrentMonth ? 'text-slate-900' : 'text-slate-400'
                        } ${isToday ? 'bg-teal-500 text-white px-2 py-0.5 rounded-full' : ''}`}>
                          {day.date.getDate()}
                        </span>
                        <div className="mt-1 space-y-1">
                          {dayEvents.slice(0, 2).map((event) => (
                            <div key={event.id} className={`text-xs px-1.5 py-0.5 rounded truncate text-white ${getEventColor(event.type)}`} title={event.title}>
                              {event.title}
                            </div>
                          ))}
                          {dayEvents.length > 2 && <div className="text-xs text-slate-500 px-1">+{dayEvents.length - 2} autres</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-lg">Prochains événements</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {upcomingEvents.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Aucun événement à venir</p>
                ) : upcomingEvents.map((event) => (
                  <div key={event.id} className="p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors cursor-pointer" data-testid={`upcoming-${event.id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${getEventColor(event.type)}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">{event.title}</p>
                        <p className="text-xs text-slate-400 truncate">{event.course_title}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <Clock className="h-3 w-3" />
                          <span>{event.date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {formatTime(event.date)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <MapPin className="h-3 w-3" /><span>{event.location}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-lg">Légende</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-teal-500" /><span className="text-sm text-slate-600">Session présentiel</span></div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500" /><span className="text-sm text-slate-600">Session distanciel</span></div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-purple-500" /><span className="text-sm text-slate-600">Terminée</span></div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AgendaPage;
